(function () {
  'use strict';

  var API_PATH = '/apps/ping/products/search';
  var PAGE_SIZE = 250;
  var RANGE_MIN = 0;
  var RANGE_MAX = 4000;
  var RANGE_GAP = 100;
  var ALLOWED_SORT_ORDERS = ['popular', 'new', 'price_asc', 'price_desc'];

  var _root = null;
  var _collectionId = '';
  var _storeExclusive = false;
  var _isApiMode = false;
  var _hasUserAdjustedPriceRange = false;
  var _currentRangeMax = RANGE_MAX;
  var _currentPage = 1;
  var _totalPages = 0;
  var _currentAbortController = null;
  // Last successfully committed API-mode state; only updated on a successful fetch response
  var _committedSnapshot = { isApiMode: false, liquidMoreHidden: false };
  var _currentQuery = '';
  var _selectedCategories = [];

  // ============================================================
  // Utilities
  // ============================================================
  function getUrlParams() {
    return new URLSearchParams(window.location.search);
  }

  function escapeHtml(str) {
    return String(str == null ? '' : str)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  }

  function formatPrice(value) {
    return Number(value || 0).toLocaleString('ja-JP') + '円';
  }

  function getTranslation(key, fallback) {
    return (window.searchTranslations && window.searchTranslations[key])
      ? String(window.searchTranslations[key])
      : String(fallback || '');
  }

  function normalizeSortOrder(value) {
    var v = String(value || '').trim().toLowerCase();
    return ALLOWED_SORT_ORDERS.indexOf(v) !== -1 ? v : '';
  }

  function getQueryFromParam() {
    var params = getUrlParams();
    return (params.get('q') || params.get('key') || '').trim();
  }

  function getSelectedCategoriesFromParam() {
    var params = getUrlParams();
    var val = (params.get('categories') || '').trim();
    return val ? val.split(',').filter(Boolean) : [];
  }

  // Return [_collectionId] if a matching category button exists, otherwise [].
  function getDefaultCategories() {
    if (!_collectionId) return [];
    var match = _root.querySelector('[data-filter="category"][data-value="' + _collectionId + '"]');
    return match ? [_collectionId] : [];
  }

  // ============================================================
  // State
  // ============================================================
  function getSortFromParam() {
    return normalizeSortOrder(getUrlParams().get('sort'));
  }

  function getCurrentPage() {
    var p = parseInt(getUrlParams().get('current_page') || getUrlParams().get('page'), 10);
    return Number.isFinite(p) && p > 0 ? p : 1;
  }

  function getDefaultPriceFilters() {
    return { min: RANGE_MIN, max: _currentRangeMax };
  }

  function isPriceInRange(value) {
    return Number.isFinite(value) && value >= RANGE_MIN && value <= _currentRangeMax;
  }

  function getQueryPriceFilters() {
    var params = getUrlParams();
    return {
      min: parseInt(params.get('price_min'), 10),
      max: parseInt(params.get('price_max'), 10),
    };
  }

  function getSliderPriceFilters() {
    var minSlider = _root.querySelector('.c-modal__filter-range[data-range="min"]');
    var maxSlider = _root.querySelector('.c-modal__filter-range[data-range="max"]');
    var minVal = minSlider ? (parseInt(minSlider.value, 10) || RANGE_MIN) : RANGE_MIN;
    var maxVal = maxSlider ? (parseInt(maxSlider.value, 10) || _currentRangeMax) : _currentRangeMax;
    return { min: minVal, max: Math.min(maxVal, _currentRangeMax) };
  }

  function hasOpenEndedMax(filters) {
    return filters.max >= _currentRangeMax;
  }

  function getRequestPriceFilters() {
    if (_hasUserAdjustedPriceRange) return getSliderPriceFilters();
    var queryFilters = getQueryPriceFilters();
    var sliderFilters = getSliderPriceFilters();
    return {
      min: Number.isFinite(queryFilters.min) ? queryFilters.min : sliderFilters.min,
      max: Number.isFinite(queryFilters.max) ? queryFilters.max : sliderFilters.max,
    };
  }

  function getRequestPriceMax(filters) {
    var queryFilters = getQueryPriceFilters();
    if (!_hasUserAdjustedPriceRange && Number.isFinite(queryFilters.max)) return queryFilters.max;
    return hasOpenEndedMax(filters) ? null : filters.max;
  }

  function getInlineToggleStates() {
    var oosEl = _root.querySelector('[data-collection-inline-stock-toggle] input[name="include-out-of-stock"]');
    var freeShipEl = _root.querySelector('#collection-filter-form input[name="free-shipping"]');
    var inStoreEl = _root.querySelector('#collection-filter-form input[name="in-store"]');
    return {
      include_out_of_stock: oosEl ? oosEl.checked : true,
      free_shipping: freeShipEl ? freeShipEl.checked : false,
      in_store: inStoreEl ? inStoreEl.checked : false,
    };
  }

  function shouldFetchOnLoad() {
    var params = getUrlParams();
    return params.has('sort') ||
      params.has('include_out_of_stock') ||
      params.has('price_min') ||
      params.has('price_max') ||
      params.has('free_shipping') ||
      params.has('in_store') ||
      (params.has('q') && params.get('q').trim() !== '') ||
      (params.has('key') && params.get('key').trim() !== '') ||
      params.has('categories') ||
      (params.has('current_page') && getCurrentPage() > 1);
  }

  // ============================================================
  // URL builder (stays on collection page)
  // ============================================================
  function buildCollectionUrl(overrides) {
    var base = new URL(window.location.pathname, window.location.origin);
    var filters = getRequestPriceFilters();
    var toggleStates = getInlineToggleStates();
    var params = overrides || {};
    var sortOrder = normalizeSortOrder(
      Object.prototype.hasOwnProperty.call(params, 'sort') ? params.sort : getSortFromParam()
    );
    var currentPage = Object.prototype.hasOwnProperty.call(params, 'current_page') ? params.current_page : 1;
    var query = Object.prototype.hasOwnProperty.call(params, 'q') ? String(params.q || '') : _currentQuery;
    var cats = Object.prototype.hasOwnProperty.call(params, 'categories') ? (params.categories || []) : _selectedCategories;

    if (sortOrder) base.searchParams.set('sort', sortOrder);

    var defaultFilters = getDefaultPriceFilters();
    if (filters.min !== defaultFilters.min) base.searchParams.set('price_min', String(filters.min));

    var requestPriceMax = getRequestPriceMax(filters);
    if (requestPriceMax !== null) base.searchParams.set('price_max', String(requestPriceMax));

    if (toggleStates.free_shipping) base.searchParams.set('free_shipping', 'true');
    if (toggleStates.in_store) base.searchParams.set('in_store', 'true');
    if (!toggleStates.include_out_of_stock) base.searchParams.set('include_out_of_stock', 'false');

    if (query) base.searchParams.set('q', query);
    if (cats && cats.length > 0) base.searchParams.set('categories', cats.join(','));

    if (currentPage > 1) base.searchParams.set('current_page', String(currentPage));

    return base.pathname + base.search;
  }

  // ============================================================
  // API URL builder
  // ============================================================
  // sortOverride: explicit sort value from the pending transition (bypasses stale URL read)
  function buildApiUrl(page, sortOverride) {
    var url = new URL(API_PATH, window.location.origin);
    var filters = getRequestPriceFilters();
    var toggleStates = getInlineToggleStates();
    var sortOrder = sortOverride !== undefined ? normalizeSortOrder(sortOverride) : getSortFromParam();
    var defaultFilters = getDefaultPriceFilters();

    var effectiveCategory = _selectedCategories.length > 0 ? _selectedCategories.join(',') : _collectionId;
    url.searchParams.set('category', effectiveCategory);
    if (_currentQuery) url.searchParams.set('key', _currentQuery);

    if (filters.min !== defaultFilters.min) url.searchParams.set('price_min', String(filters.min));

    var requestPriceMax = getRequestPriceMax(filters);
    if (requestPriceMax !== null) url.searchParams.set('price_max', String(requestPriceMax));

    url.searchParams.set('include_out_of_stock', toggleStates.include_out_of_stock ? 'true' : 'false');
    if (toggleStates.free_shipping) url.searchParams.set('free_shipping', 'true');
    if (toggleStates.in_store) url.searchParams.set('in_store', 'true');
    url.searchParams.set('page_size', String(PAGE_SIZE));
    url.searchParams.set('current_page', String(page || 1));
    // Always default to collection order when no explicit sort is selected.
    var effectiveSort = sortOrder || 'collection_order';
    url.searchParams.set('sort', effectiveSort);
    if (_storeExclusive) url.searchParams.set('store_exclusive_only', 'true');

    return url.toString();
  }

  // ============================================================
  // Product card rendering (mirrors js_search.js)
  // ============================================================
  function getFirstDefinedValue(source, paths) {
    for (var i = 0; i < paths.length; i++) {
      var val = paths[i].split('.').reduce(function (obj, key) { return obj ? obj[key] : undefined; }, source);
      if (val && typeof val === 'object' && Object.prototype.hasOwnProperty.call(val, 'value')) val = val.value;
      if (val !== undefined && val !== null && val !== '') return val;
    }
    return '';
  }

  function getBooleanMeta(product, paths) {
    var val = getFirstDefinedValue(product, paths);
    if (typeof val === 'boolean') return val;
    if (typeof val === 'string') return val === 'true';
    return Boolean(val);
  }

  function normalizeProductTags(product) {
    var rawTags = product.tags;
    if (Array.isArray(rawTags)) return rawTags.map(function (t) { return String(t || '').trim(); }).filter(Boolean);
    if (typeof rawTags === 'string') return rawTags.split(',').map(function (t) { return t.trim(); }).filter(Boolean);
    return [];
  }

  function hasProductTag(tags, tagName) {
    return tags.some(function (t) { return t.toLowerCase() === String(tagName || '').toLowerCase(); });
  }

  function resolveButtonType(product) {
    var status = String(getFirstDefinedValue(product, ['status']) || '').trim().toLowerCase();
    return status || (product.available ? 'available' : 'out_of_stock');
  }

  function isPreorderSale(product) {
    return getFirstDefinedValue(product, ['sales_method']) === '予約販売';
  }

  function getEndedButtonLabel(product) {
    if (getBooleanMeta(product, ['ec_exclusive', 'metafields.yokumoku.ec_exclusive'])) {
      return 'オンライン販売終了しました';
    }
    return '販売終了しました';
  }

  function renderProductTags(product, tags, buttonType) {
    var configuredTags = Array.isArray(window.searchAvailableItemTags) ? window.searchAvailableItemTags : [];
    var tagHtml = [];

    configuredTags.forEach(function (ct) {
      if (hasProductTag(tags, ct)) tagHtml.push('<span class="c-tag">' + escapeHtml(ct) + '</span>');
    });

    if (getBooleanMeta(product, ['flags_wrapping'])) tagHtml.push('<span class="c-tag">ラッピング対応</span>');
    if (getBooleanMeta(product, ['flags_noshi'])) tagHtml.push('<span class="c-tag">のし対応</span>');
    if (getBooleanMeta(product, ['flags_is_social_gift_available'])) tagHtml.push('<span class="c-tag">eギフト対応</span>');
    if (getBooleanMeta(product, ['ec_exclusive'])) tagHtml.push('<span class="c-tag">オンライン限定</span>');
    if (getFirstDefinedValue(product, ['temperature_zone']) === '冷凍') tagHtml.push('<span class="c-tag">冷凍</span>');
    if (buttonType === 'store_only') tagHtml.push('<span class="c-tag">店舗のみ</span>');
    if (buttonType === 'partial_online') tagHtml.push('<span class="c-tag">一部オンライン限定</span>');

    if (!tagHtml.length) return '';
    return '<div class="c-tags">' + tagHtml.join('') + '</div>';
  }

  function renderProductButton(product, buttonType, url) {
    var variantId = getFirstDefinedValue(product, ['variant_id']);
    var oosMessage = escapeHtml(getFirstDefinedValue(product, ['oos_message']) || '');

    if (buttonType === 'available') {
      var label = isPreorderSale(product)
        ? '予約商品をカートに入れる'
        : getTranslation('add_to_cart', 'カートに入れる');
      var setitemsAttr = '';
      var setitems = product.setitems;
      if (setitems && Array.isArray(setitems) && setitems.length > 0) {
        var csv = setitems.map(function (item) {
          if (typeof item === 'object' && item !== null && item.id != null) {
            var m = String(item.id).match(/(\d+)$/);
            return (m ? m[1] : String(item.id)) + ':' + (item.quantity || 1);
          }
          var m2 = String(item).match(/(\d+)$/);
          return (m2 ? m2[1] : String(item)) + ':1';
        }).join(',');
        setitemsAttr = ' data-setitems="' + escapeHtml(csv) + '" data-product-title="' + escapeHtml(product.title) + '"';
      }
      return '<a href="#" class="c-btn__cart js-add-to-cart" data-product-id="' +
        escapeHtml(variantId) + '" data-product-url="' + escapeHtml(url) + '"' + setitemsAttr + '>' +
        escapeHtml(label) + '</a>';
    }
    if (buttonType === 'sale_not_started') {
      var snsl = isPreorderSale(product) ? '予約販売開始前' : getTranslation('sale_not_started', '販売開始前');
      return '<a href="' + escapeHtml(url) + '" class="c-btn__cart c-btn__cart--period">' + escapeHtml(snsl) + '</a>';
    }
    if (buttonType === 'sale_ended') {
      return '<a href="' + escapeHtml(url) + '" class="c-btn__cart c-btn__cart--period">' + escapeHtml(getEndedButtonLabel(product)) + '</a>';
    }
    if (buttonType === 'restock') {
      return '<a href="' + escapeHtml(url) + '" class="c-btn__cart c-btn__cart--restock">' + escapeHtml(getTranslation('restock', '再入荷お知らせ')) + '</a>';
    }
    if (buttonType === 'store_only') {
      return '<a href="' + escapeHtml(url) + '" class="c-btn__cart c-btn__cart--store">' + escapeHtml(getTranslation('store_only', '店舗限定')) + '</a>';
    }
    if (buttonType === 'partial_online') {
      return '<a href="' + escapeHtml(url) + '" class="c-btn__cart c-btn__cart--period">' + escapeHtml(getTranslation('partial_online', '一部オンライン限定')) + '</a>';
    }
    if (buttonType === 'out_of_stock') {
      return '<a href="' + escapeHtml(url) + '" class="c-btn__cart c-btn__cart--out-stock">' +
        (oosMessage || escapeHtml(getTranslation('out_of_stock', 'ただいま品切れしております'))) + '</a>';
    }
    return '<a href="' + escapeHtml(url) + '" class="c-btn__cart c-btn__cart--out-stock">' +
      (oosMessage || escapeHtml(getEndedButtonLabel(product))) + '</a>';
  }

  function renderProductCard(product) {
    var title = escapeHtml(product.title);
    var url = escapeHtml(product.url || (product.handle ? '/products/' + product.handle : '#'));
    var image = product.image ? escapeHtml(product.image) : '';
    var price = formatPrice(product.price);
    var priceYen = product.price != null ? Math.round(Number(product.price)) : null;
    var rawId = getFirstDefinedValue(product, ['product_id', 'id']);
    var idMatch = rawId != null ? String(rawId).match(/(\d+)$/) : null;
    var numericProductId = idMatch ? idMatch[1] : '';
    var discountAttrs = (priceYen != null && priceYen > 0 && numericProductId)
      ? ' data-discount-price="' + priceYen + '" data-product-id="' + escapeHtml(numericProductId) + '"'
      : '';
    var tags = normalizeProductTags(product);
    var buttonType = resolveButtonType(product);

    var salesStartAt = getFirstDefinedValue(product, ['sales_start_datetime', 'sales_start_at']);
    var showNewBadge = false;
    if (salesStartAt) {
      var startMs = new Date(salesStartAt).getTime();
      var nowMs = Date.now();
      if (!isNaN(startMs) && nowMs >= startMs && nowMs - startMs <= 30 * 24 * 60 * 60 * 1000) {
        showNewBadge = true;
      }
    }

    var customerType = window.yokumokuCustomerType || '通販顧客';
    var nationwideTag = '全国送料無料_' + customerType;
    var showNationwideBadge = hasProductTag(tags, nationwideTag);
    var showFreeBadge = !showNationwideBadge && hasProductTag(tags, '送料無料');

    var badgeHtml = [];
    if (showNewBadge) badgeHtml.push('<span class="c-product-card__label c-product-card__label--new">NEW</span>');
    if (showNationwideBadge) badgeHtml.push('<span class="c-product-card__label c-product-card__label--free-shipping">全国送料無料</span>');
    else if (showFreeBadge) badgeHtml.push('<span class="c-product-card__label c-product-card__label--free-shipping">オンライン送料無料</span>');

    return '<li class="c-grid__item" data-search-result-item data-search-type="product">' +
      '<article>' +
      '<div class="c-product-card">' +
      '<a href="' + url + '" class="c-product-card__img">' +
      (image ? '<img src="' + image + '" alt="' + title + '" loading="lazy">' : '<div class="c-product-card__img-placeholder"></div>') +
      '</a>' +
      (badgeHtml.length ? '<div class="c-product-card__badge">' + badgeHtml.join('') + '</div>' : '') +
      '<div class="c-product-card__body">' +
      '<div class="js-matchHeight-item">' +
      '<a href="' + url + '" class="c-product-card__title">' + title + '</a>' +
      renderProductTags(product, tags, buttonType) +
      '<p class="c-product-card__price"' + discountAttrs + '>' + price + '<small>(税込)</small></p>' +
      '</div>' +
      renderProductButton(product, buttonType, url) +
      '</div>' +
      '</div>' +
      '</article>' +
      '</li>';
  }

  // ============================================================
  // Filter summary tags
  // ============================================================
  function updateFilterSummary() {
    var summaryEl = _root.querySelector('[data-collection-filter-summary]');
    var tagsEl = _root.querySelector('[data-collection-filter-tags]');
    if (!summaryEl || !tagsEl) return;

    tagsEl.innerHTML = '';
    var filters = getRequestPriceFilters();
    var toggleStates = getInlineToggleStates();
    var defaultFilters = getDefaultPriceFilters();
    var isDefaultRange = filters.min === defaultFilters.min && filters.max === defaultFilters.max;

    if (_currentQuery) {
      var kwTag = document.createElement('span');
      kwTag.className = 'c-tag';
      kwTag.textContent = _currentQuery;
      tagsEl.appendChild(kwTag);
    }

    _selectedCategories.forEach(function (catId) {
      var catBtn = _root.querySelector('[data-filter="category"][data-value="' + catId + '"]');
      var catLabel = catBtn ? catBtn.textContent.trim() : catId;
      var catTag = document.createElement('span');
      catTag.className = 'c-tag';
      catTag.textContent = catLabel;
      tagsEl.appendChild(catTag);
    });

    if (!isDefaultRange) {
      var priceLabel = hasOpenEndedMax(filters)
        ? formatPrice(filters.min) + '〜'
        : formatPrice(filters.min) + '〜' + formatPrice(filters.max);
      var priceTag = document.createElement('span');
      priceTag.className = 'c-tag';
      priceTag.textContent = priceLabel;
      tagsEl.appendChild(priceTag);
    }

    if (toggleStates.free_shipping) {
      var freeTag = document.createElement('span');
      freeTag.className = 'c-tag';
      freeTag.textContent = 'オンライン送料無料';
      tagsEl.appendChild(freeTag);
    }

    if (toggleStates.in_store) {
      var inStoreTag = document.createElement('span');
      inStoreTag.className = 'c-tag';
      inStoreTag.textContent = '店舗取り扱いあり';
      tagsEl.appendChild(inStoreTag);
    }

    if (!toggleStates.include_out_of_stock) {
      var oosTag = document.createElement('span');
      oosTag.className = 'c-tag';
      oosTag.textContent = '在庫あり';
      tagsEl.appendChild(oosTag);
    }

    summaryEl.hidden = tagsEl.children.length === 0;
  }

  // ============================================================
  // Fetch and render
  // ============================================================
  function enterApiMode() {
    if (_isApiMode) return;
    _isApiMode = true;
    // Hide Liquid-rendered load-more (paginate mode)
    var liquidMore = _root.querySelector('[data-collection-load-more-wrap]');
    if (liquidMore) liquidMore.hidden = true;
  }

  function setLoadingState(isLoading, message) {
    var el = _root.querySelector('[data-collection-loading]');
    if (!el) return;
    if (isLoading) {
      el.textContent = message || '';
      el.hidden = false;
    } else {
      el.hidden = true;
      el.textContent = '';
    }
  }

  // fetchResults(page, append, opts)
  // opts.pendingUrl   — committed to history only on success (omit for popstate/on-load)
  // opts.sortOverride — pending sort bypasses stale URL read in buildApiUrl
  function fetchResults(page, append, opts) {
    var pendingUrl = opts && opts.pendingUrl;
    var sortOverride = opts && opts.sortOverride;

    // Abort any in-flight request
    if (_currentAbortController) _currentAbortController.abort();
    _currentAbortController = typeof AbortController !== 'undefined' ? new AbortController() : null;

    enterApiMode();

    var liquidMore = _root.querySelector('[data-collection-load-more-wrap]');
    var grid = _root.querySelector('[data-collection-product-grid]');
    var apiMoreWrap = _root.querySelector('[data-collection-api-more-wrap]');
    var apiMoreBtn = _root.querySelector('[data-collection-api-more-button]');
    var apiMoreStatus = _root.querySelector('[data-collection-api-more-status]');
    var emptyState = _root.querySelector('[data-collection-empty-state]');

    // Reset both loading modes; the new request takes full ownership before activating its mode
    setLoadingState(false);
    if (apiMoreStatus) { apiMoreStatus.hidden = true; apiMoreStatus.textContent = ''; }
    if (apiMoreBtn) apiMoreBtn.classList.remove('is-loading');

    if (append) {
      if (apiMoreStatus) { apiMoreStatus.hidden = false; apiMoreStatus.textContent = 'Loading...'; }
      if (apiMoreBtn) apiMoreBtn.classList.add('is-loading');
    } else {
      setLoadingState(true, 'Loading...');
    }

    var fetchOptions = { method: 'GET', headers: { Accept: 'application/json' }, credentials: 'same-origin' };
    if (_currentAbortController) fetchOptions.signal = _currentAbortController.signal;

    return fetch(buildApiUrl(page, sortOverride), fetchOptions)
      .then(function (res) {
        if (!res.ok) throw new Error('Collection filter API error: ' + res.status);
        return res.json();
      })
      .then(function (data) {
        // Commit navigation state only after a successful response
        if (pendingUrl) history.pushState({}, '', pendingUrl);

        var products = Array.isArray(data.product_list) ? data.product_list : [];
        _currentPage = parseInt(data.current_page, 10) || page;
        _totalPages = parseInt(data.total_page, 10) || 0;

        if (append) {
          if (apiMoreStatus) apiMoreStatus.hidden = true;
          if (apiMoreBtn) apiMoreBtn.classList.remove('is-loading');
        } else {
          setLoadingState(false);
        }

        if (append && grid) {
          var tmp = document.createElement('ul');
          tmp.innerHTML = products.map(renderProductCard).join('');
          while (tmp.firstChild) grid.appendChild(tmp.firstChild);
          if (window.DiscountPrice) window.DiscountPrice.apply(grid);
        } else if (grid) {
          grid.innerHTML = products.map(renderProductCard).join('');
          if (window.DiscountPrice) window.DiscountPrice.apply(grid);
        }

        requestAnimationFrame(function () {
          if (typeof window.initMatchHeight === 'function') {
            window.initMatchHeight();
          } else if (typeof initMatchHeight === 'function') {
            initMatchHeight();
          }
        });

        if (emptyState) emptyState.hidden = products.length > 0;

        var hasMore = typeof data.has_next_page === 'boolean'
          ? data.has_next_page
          : _currentPage < _totalPages;
        if (apiMoreWrap) apiMoreWrap.hidden = !hasMore;

        // Record this as the new rollback baseline (committed state)
        _committedSnapshot = { isApiMode: _isApiMode, liquidMoreHidden: liquidMore ? liquidMore.hidden : false };

        updateFilterSummary();
      })
      .catch(function (err) {
        if (err.name === 'AbortError') return; // superseded by a newer request
        // Restore to the last successfully committed state.
        // Using _committedSnapshot (not a per-request snapshot) ensures chained
        // abort→failure sequences always roll back to the original committed state,
        // not to an intermediate uncommitted state from an aborted request.
        _isApiMode = _committedSnapshot.isApiMode;
        if (liquidMore) liquidMore.hidden = _committedSnapshot.liquidMoreHidden;
        // Sync all UI controls to the committed URL (pendingUrl was never pushed on failure)
        _hasUserAdjustedPriceRange = false;
        syncFiltersFromQuery();
        syncSortDropdown();
        updatePriceRangeUI();
        if (append) {
          if (apiMoreStatus) { apiMoreStatus.hidden = false; apiMoreStatus.textContent = 'エラーが発生しました'; }
          if (apiMoreBtn) apiMoreBtn.classList.remove('is-loading');
        } else {
          setLoadingState(true, 'エラーが発生しました');
        }
        console.error('[CollectionFilter]', err);
      });
  }

  function applyFilters(overrides) {
    var params = overrides || {};
    var nextUrl = buildCollectionUrl(params);
    var sortOverride = Object.prototype.hasOwnProperty.call(params, 'sort') ? params.sort : undefined;
    return fetchResults(1, false, { pendingUrl: nextUrl, sortOverride: sortOverride });
  }

  // ============================================================
  // Price range UI
  // ============================================================
  function updatePriceRangeUI() {
    var filters = getSliderPriceFilters();
    var minPercent = _currentRangeMax ? (filters.min / _currentRangeMax) * 100 : 0;
    var maxPercent = _currentRangeMax ? (filters.max / _currentRangeMax) * 100 : 100;
    var trackEl = _root.querySelector('.c-modal__filter-range-track');
    var priceValuesEl = _root.querySelector('.c-modal__filter-price-values');
    var minLabel = _root.querySelector('.c-modal__filter-price-min');
    var maxLabel = _root.querySelector('.c-modal__filter-price-max');

    if (minLabel) minLabel.textContent = formatPrice(filters.min) + '〜';
    if (maxLabel) maxLabel.textContent = hasOpenEndedMax(filters) ? formatPrice(_currentRangeMax) + '〜' : '〜' + formatPrice(filters.max);
    if (trackEl) {
      trackEl.style.setProperty('--range-min', minPercent + '%');
      trackEl.style.setProperty('--range-max', (100 - maxPercent) + '%');
    }
    if (priceValuesEl) {
      priceValuesEl.style.setProperty('--range-min', minPercent + '%');
      priceValuesEl.style.setProperty('--range-max', (100 - maxPercent) + '%');
    }
  }

  function syncFiltersFromQuery() {
    var params = getUrlParams();
    var min = parseInt(params.get('price_min'), 10);
    var max = parseInt(params.get('price_max'), 10);
    var defaultFilters = getDefaultPriceFilters();
    var minForSlider = isPriceInRange(min) ? min : defaultFilters.min;
    var maxForSlider = isPriceInRange(max) ? max : defaultFilters.max;
    var includeOosParam = params.get('include_out_of_stock');
    var includeOos = includeOosParam === null ? true : (includeOosParam === 'true' || includeOosParam === 'on');
    var freeShipping = params.get('free_shipping') === 'true';
    var inStore = params.get('in_store') === 'true';

    var minSlider = _root.querySelector('.c-modal__filter-range[data-range="min"]');
    var maxSlider = _root.querySelector('.c-modal__filter-range[data-range="max"]');
    if (minSlider) minSlider.value = minForSlider;
    if (maxSlider) maxSlider.value = maxForSlider;

    _root.querySelectorAll('input[name="include-out-of-stock"]').forEach(function (el) { el.checked = includeOos; });
    _root.querySelectorAll('input[name="free-shipping"]').forEach(function (el) { el.checked = freeShipping; });
    _root.querySelectorAll('input[name="in-store"]').forEach(function (el) { el.checked = inStore; });

    var query = getQueryFromParam();
    _currentQuery = query;
    var searchInput = _root.querySelector('[data-collection-search-input]');
    if (searchInput) searchInput.value = query;
    var filterQueryInput = _root.querySelector('[data-collection-filter-query]');
    if (filterQueryInput) filterQueryInput.value = query;

    var cats = getSelectedCategoriesFromParam();
    _selectedCategories = cats.length > 0 ? cats : getDefaultCategories();
    syncCategoryButtonStates();
  }

  // Mark category buttons as active based on explicit _selectedCategories state only.
  function syncCategoryButtonStates() {
    _root.querySelectorAll('[data-filter="category"]').forEach(function (btn) {
      btn.classList.toggle('c-tag--active', _selectedCategories.indexOf(btn.dataset.value) !== -1);
    });
  }

  function initPriceRangeSlider() {
    var minSlider = _root.querySelector('.c-modal__filter-range[data-range="min"]');
    var maxSlider = _root.querySelector('.c-modal__filter-range[data-range="max"]');
    if (!minSlider || !maxSlider) return;

    minSlider.addEventListener('input', function () {
      _hasUserAdjustedPriceRange = true;
      var minVal = parseInt(minSlider.value, 10);
      var maxVal = parseInt(maxSlider.value, 10);
      if (minVal > maxVal - RANGE_GAP) minSlider.value = maxVal - RANGE_GAP;
      updatePriceRangeUI();
    });

    maxSlider.addEventListener('input', function () {
      _hasUserAdjustedPriceRange = true;
      var minVal = parseInt(minSlider.value, 10);
      var maxVal = parseInt(maxSlider.value, 10);
      if (maxVal < minVal + RANGE_GAP) maxSlider.value = minVal + RANGE_GAP;
      updatePriceRangeUI();
    });

    updatePriceRangeUI();
  }

  // ============================================================
  // Search form
  // ============================================================
  function initSearchForm() {
    var form = _root.querySelector('[data-collection-search-form]');
    if (!form) return;
    form.addEventListener('submit', function (e) {
      e.preventDefault();
      var input = form.querySelector('[data-collection-search-input]');
      var query = input ? input.value.trim() : '';
      _currentQuery = query;
      var filterQueryInput = _root.querySelector('[data-collection-filter-query]');
      if (filterQueryInput) filterQueryInput.value = query;
      applyFilters({ q: query, categories: _selectedCategories, current_page: 1 });
    });
  }

  // ============================================================
  // Category tags
  // ============================================================
  function initCategoryTags() {
    _root.querySelectorAll('[data-filter="category"]').forEach(function (btn) {
      btn.addEventListener('click', function () {
        var val = btn.dataset.value;
        var idx = _selectedCategories.indexOf(val);
        if (idx !== -1) {
          _selectedCategories.splice(idx, 1);
        } else {
          _selectedCategories.push(val);
        }
        syncCategoryButtonStates();
      });
    });
  }

  // ============================================================
  // Sort dropdown
  // ============================================================
  function initSortDropdown() {
    var dropdown = _root.querySelector('[data-collection-sort-dropdown]');
    if (!dropdown) return;

    var toggle = dropdown.querySelector('.p-search-products__toggle');
    var options = dropdown.querySelector('.p-search-products__options');
    if (!toggle || !options) return;

    var defaultLabel = toggle.textContent.trim();
    // Store so popstate handler can restore it without re-capturing closure
    toggle.dataset.defaultLabel = defaultLabel;
    var currentSort = getSortFromParam();
    var selectedLabel = defaultLabel;

    options.querySelectorAll('li').forEach(function (li) {
      var optionValue = normalizeSortOrder(li.dataset.value);
      var isDefault = li.dataset.defaultSort === 'true';
      var isSelected = isDefault ? currentSort === '' : optionValue === currentSort;
      li.classList.toggle('is-selected', isSelected);
      if (isSelected && !isDefault) selectedLabel = li.textContent.trim();
    });

    toggle.textContent = selectedLabel;
    toggle.classList.toggle('is-selected', selectedLabel !== defaultLabel);

    toggle.addEventListener('click', function () {
      dropdown.classList.toggle('is-open');
      options.classList.toggle('show');
    });

    options.addEventListener('click', function (e) {
      var li = e.target.closest('li');
      // Treat aria-disabled options as no-ops regardless of state
      if (!li || li.classList.contains('is-disabled') || li.getAttribute('aria-disabled') === 'true') return;
      var isDefaultOption = li.dataset.defaultSort === 'true';
      var sortOrder = normalizeSortOrder(li.dataset.value);

      // Optimistic UI update (rolled back via URL-based sync in fetchResults catch)
      options.querySelectorAll('li').forEach(function (opt) { opt.classList.remove('is-selected'); });
      li.classList.add('is-selected');
      var newLabel = isDefaultOption ? defaultLabel : li.textContent.trim();
      toggle.textContent = newLabel;
      toggle.classList.toggle('is-selected', newLabel !== defaultLabel);

      applyFilters({ sort: sortOrder, current_page: 1 });
      dropdown.classList.remove('is-open');
      options.classList.remove('show');
    });

    document.addEventListener('click', function (e) {
      if (!dropdown.contains(e.target)) {
        dropdown.classList.remove('is-open');
        options.classList.remove('show');
      }
    });
  }

  // ============================================================
  // OOS inline toggle
  // ============================================================
  function initOosToggle() {
    var toggleWrap = _root.querySelector('[data-collection-inline-stock-toggle]');
    if (!toggleWrap) return;
    var input = toggleWrap.querySelector('input[name="include-out-of-stock"]');
    if (!input) return;
    input.addEventListener('change', function () {
      // Optimistic: sync modal OOS immediately; rolled back via URL-based sync in fetchResults catch
      var modalOos = _root.querySelector('#collection-filter-form input[name="include-out-of-stock"]');
      if (modalOos) modalOos.checked = input.checked;
      applyFilters({ current_page: 1 });
    });
  }

  // ============================================================
  // Filter modal
  // ============================================================
  function initFilterModal() {
    var modal = document.getElementById('modal-collection-filter');
    if (!modal) return;

    var filterForm = document.getElementById('collection-filter-form');
    var clearBtn = modal.querySelector('[data-collection-filter-clear]');
    // Set to true in modal submit to distinguish submit-close from dismiss-close
    var _pendingSubmit = false;

    // Sync modal OOS with inline OOS on modal open
    var filterOpenBtn = _root.querySelector('[data-collection-filter-open]');
    if (filterOpenBtn) {
      filterOpenBtn.addEventListener('click', function () {
        var inlineOos = _root.querySelector('[data-collection-inline-stock-toggle] input[name="include-out-of-stock"]');
        var modalOos = filterForm ? filterForm.querySelector('input[name="include-out-of-stock"]') : null;
        if (inlineOos && modalOos) modalOos.checked = inlineOos.checked;
        var filterQueryInput = filterForm ? filterForm.querySelector('[data-collection-filter-query]') : null;
        if (filterQueryInput) filterQueryInput.value = _currentQuery;
        // Trigger price range layout update
        requestAnimationFrame(function () { updatePriceRangeUI(); });
      });
    }

    if (clearBtn) {
      clearBtn.addEventListener('click', function () {
        _hasUserAdjustedPriceRange = true;
        var defaultFilters = getDefaultPriceFilters();
        var minSlider = _root.querySelector('.c-modal__filter-range[data-range="min"]');
        var maxSlider = _root.querySelector('.c-modal__filter-range[data-range="max"]');
        if (minSlider) minSlider.value = defaultFilters.min;
        if (maxSlider) maxSlider.value = defaultFilters.max;
        if (filterForm) {
          filterForm.querySelectorAll('input[name="free-shipping"]').forEach(function (el) { el.checked = false; });
          filterForm.querySelectorAll('input[name="in-store"]').forEach(function (el) { el.checked = false; });
          filterForm.querySelectorAll('input[name="include-out-of-stock"]').forEach(function (el) { el.checked = true; });
          var filterQueryInput = filterForm.querySelector('[data-collection-filter-query]');
          if (filterQueryInput) filterQueryInput.value = '';
        }
        _selectedCategories = getDefaultCategories();
        syncCategoryButtonStates();
        _currentQuery = '';
        var searchInput = _root.querySelector('[data-collection-search-input]');
        if (searchInput) searchInput.value = '';
        updatePriceRangeUI();
      });
    }

    if (filterForm) {
      filterForm.addEventListener('submit', function (e) {
        e.preventDefault();

        // Read values from modal form
        var modalOos = filterForm.querySelector('input[name="include-out-of-stock"]');
        var modalFreeShip = filterForm.querySelector('input[name="free-shipping"]');
        var modalInStore = filterForm.querySelector('input[name="in-store"]');

        // Read keyword
        var filterQueryInput = filterForm.querySelector('[data-collection-filter-query]');
        var query = filterQueryInput ? filterQueryInput.value.trim() : _currentQuery;
        _currentQuery = query;
        var searchInput = _root.querySelector('[data-collection-search-input]');
        if (searchInput) searchInput.value = query;

        // Read selected categories
        var cats = [];
        filterForm.querySelectorAll('[data-filter="category"].c-tag--active').forEach(function (btn) {
          cats.push(btn.dataset.value);
        });
        _selectedCategories = cats;

        // Sync inline toggle with modal result
        var inlineOos = _root.querySelector('[data-collection-inline-stock-toggle] input[name="include-out-of-stock"]');
        if (inlineOos && modalOos) inlineOos.checked = modalOos.checked;

        // Build URL from modal state
        var base = new URL(window.location.pathname, window.location.origin);
        var sortOrder = getSortFromParam();
        if (sortOrder) base.searchParams.set('sort', sortOrder);

        var minSlider = _root.querySelector('.c-modal__filter-range[data-range="min"]');
        var maxSlider = _root.querySelector('.c-modal__filter-range[data-range="max"]');
        var minVal = minSlider ? parseInt(minSlider.value, 10) : RANGE_MIN;
        var maxVal = maxSlider ? parseInt(maxSlider.value, 10) : _currentRangeMax;

        if (minVal > RANGE_MIN) base.searchParams.set('price_min', String(minVal));
        if (maxVal < _currentRangeMax) base.searchParams.set('price_max', String(maxVal));
        if (modalFreeShip && modalFreeShip.checked) base.searchParams.set('free_shipping', 'true');
        if (modalInStore && modalInStore.checked) base.searchParams.set('in_store', 'true');
        if (modalOos && !modalOos.checked) base.searchParams.set('include_out_of_stock', 'false');
        if (query) base.searchParams.set('q', query);
        if (cats.length > 0) base.searchParams.set('categories', cats.join(','));

        var nextUrl = base.pathname + base.search;

        // Close modal before fetching
        _pendingSubmit = true;
        var closeEvent = new Event('click', { bubbles: true });
        var closeBtn = modal.querySelector('.js-modal-close');
        if (closeBtn) closeBtn.dispatchEvent(closeEvent);

        _hasUserAdjustedPriceRange = true;
        fetchResults(1, false, { pendingUrl: nextUrl }); // pushState deferred to success
      });
    }

    // On dismiss (close without submit): discard draft and restore committed URL state.
    // _pendingSubmit distinguishes a submit-triggered close from a user dismiss.
    modal.querySelectorAll('.js-modal-close').forEach(function (closeEl) {
      closeEl.addEventListener('click', function () {
        if (!_pendingSubmit) {
          _hasUserAdjustedPriceRange = false;
          syncFiltersFromQuery();
          updatePriceRangeUI();
        }
        _pendingSubmit = false;
      });
    });

    // Escape key closes the modal in js_common.js without triggering .js-modal-close click events.
    // Apply the same draft-discard logic independently.
    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape' && modal.classList.contains('is-active')) {
        _hasUserAdjustedPriceRange = false;
        syncFiltersFromQuery();
        updatePriceRangeUI();
      }
    });
  }

  // ============================================================
  // API load-more
  // ============================================================
  function initApiLoadMore() {
    var apiMoreBtn = _root.querySelector('[data-collection-api-more-button]');
    if (!apiMoreBtn) return;

    apiMoreBtn.addEventListener('click', function (e) {
      e.preventDefault();
      if (apiMoreBtn.classList.contains('is-loading')) return;
      var nextPage = _currentPage + 1;
      var nextUrl = buildCollectionUrl({ current_page: nextPage });
      fetchResults(nextPage, true, { pendingUrl: nextUrl }); // pushState deferred to success
    });
  }

  // ============================================================
  // Sync sort dropdown label from current URL (used on popstate)
  // ============================================================
  function syncSortDropdown() {
    var dropdown = _root.querySelector('[data-collection-sort-dropdown]');
    if (!dropdown) return;
    var toggle = dropdown.querySelector('.p-search-products__toggle');
    var options = dropdown.querySelector('.p-search-products__options');
    if (!toggle || !options) return;

    var defaultLabel = toggle.dataset.defaultLabel || toggle.textContent.trim();
    var currentSort = getSortFromParam();
    var selectedLabel = defaultLabel;

    options.querySelectorAll('li').forEach(function (li) {
      var optionValue = normalizeSortOrder(li.dataset.value);
      var isDefault = li.dataset.defaultSort === 'true';
      var isSelected = isDefault ? currentSort === '' : optionValue === currentSort;
      li.classList.toggle('is-selected', isSelected);
      if (isSelected && !isDefault) selectedLabel = li.textContent.trim();
    });

    toggle.textContent = selectedLabel;
    toggle.classList.toggle('is-selected', selectedLabel !== defaultLabel);
  }

  // ============================================================
  // Init
  // ============================================================
  function init() {
    _root = document.querySelector('[data-collection-search-root]');
    if (!_root) return;

    _collectionId = _root.dataset.collectionId || '';
    if (!_collectionId) return;

    _storeExclusive = _root.dataset.collectionHandle === 'store-exclusive';

    syncFiltersFromQuery();
    initSearchForm();
    initCategoryTags();
    initSortDropdown();
    initOosToggle();
    initFilterModal();
    initPriceRangeSlider();
    initApiLoadMore();

    // Restore controls and results on browser Back/Forward
    window.addEventListener('popstate', function () {
      _hasUserAdjustedPriceRange = false;
      syncFiltersFromQuery();
      syncSortDropdown();
      updatePriceRangeUI();
      if (_isApiMode || shouldFetchOnLoad()) {
        fetchResults(getCurrentPage(), false, {}); // no pendingUrl — URL already set by browser
      }
    });

    if (shouldFetchOnLoad()) {
      fetchResults(getCurrentPage(), false, {});
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
